/**
 * Sugestões — página do dentista
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Chip, CircularProgress, Alert, Divider, MenuItem, Select,
  FormControl, InputLabel, Avatar,
} from '@mui/material';
import LightbulbIcon      from '@mui/icons-material/Lightbulb';
import SendIcon           from '@mui/icons-material/Send';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import HourglassTopIcon   from '@mui/icons-material/HourglassTop';
import RocketLaunchIcon   from '@mui/icons-material/RocketLaunch';
import ThumbUpIcon        from '@mui/icons-material/ThumbUp';
import { enviarSugestao, listarMinhas } from '../../services/sugestaoService';

const ORANGE = '#F97316';
const GREEN  = '#10B981';
const BLUE   = '#2563EB';
const AMBER  = '#F59E0B';
const PURPLE = '#7C3AED';
const RED    = '#EF4444';
const DARK   = '#0F172A';

const STATUS_MAP = {
  pendente:       { label: 'Recebida',     color: AMBER,  icon: <HourglassTopIcon  sx={{ fontSize: 13 }} /> },
  em_analise:     { label: 'Em análise',   color: BLUE,   icon: <HourglassTopIcon  sx={{ fontSize: 13 }} /> },
  planejado:      { label: 'Planejado',    color: PURPLE, icon: <RocketLaunchIcon  sx={{ fontSize: 13 }} /> },
  implementado:   { label: 'Implementado', color: GREEN,  icon: <CheckCircleIcon   sx={{ fontSize: 13 }} /> },
  recusado:       { label: 'Não aplicável',color: RED,    icon: null },
};

const CATEGORIAS = [
  { value: 'melhoria',      label: '✨ Melhoria'      },
  { value: 'funcionalidade',label: '🆕 Nova funcionalidade' },
  { value: 'bug',           label: '🐛 Correção de erro'   },
  { value: 'outro',         label: '💬 Outro'           },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

export default function Sugestoes() {
  const [sugestoes, setSugestoes]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [enviando, setEnviando]     = useState(false);
  const [sucesso, setSucesso]       = useState(false);

  const [titulo, setTitulo]         = useState('');
  const [descricao, setDescricao]   = useState('');
  const [categoria, setCategoria]   = useState('melhoria');
  const [erro, setErro]             = useState('');

  const carregar = async () => {
    try {
      const res = await listarMinhas();
      setSugestoes(res.data || []);
    } catch { /* silencioso */ }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const handleEnviar = async () => {
    if (!titulo.trim() || !descricao.trim()) {
      setErro('Preencha o título e a descrição.');
      return;
    }
    setErro('');
    setEnviando(true);
    try {
      await enviarSugestao({ titulo, descricao, categoria });
      setTitulo('');
      setDescricao('');
      setCategoria('melhoria');
      setSucesso(true);
      setTimeout(() => setSucesso(false), 6000);
      carregar();
    } catch {
      setErro('Erro ao enviar sugestão. Tente novamente.');
    }
    setEnviando(false);
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 3 }}>

      {/* ── Hero ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: `${ORANGE}18`, boxShadow: `0 0 0 6px ${ORANGE}10` }}>
          <LightbulbIcon sx={{ color: ORANGE, fontSize: 28 }} />
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: DARK }}>
            Sugestões de Melhoria
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
            Sua ideia pode entrar na próxima atualização do sistema ✨
          </Typography>
        </Box>
      </Box>

      {/* ── Banner motivacional ── */}
      <Card elevation={0} sx={{
        mb: 3, borderRadius: 3,
        background: `linear-gradient(135deg, ${ORANGE}14 0%, ${PURPLE}0a 100%)`,
        border: `1px solid ${ORANGE}25`,
      }}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK, mb: 0.5 }}>
            💡 Como funciona?
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.6 }}>
            Você envia sua sugestão → nossa equipe analisa → as melhores entram no roadmap e são
            implementadas assim que possível. Cada sugestão recebe um status para você acompanhar. 🚀
          </Typography>
        </CardContent>
      </Card>

      {/* ── Formulário ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: DARK, mb: 2 }}>
            Nova sugestão
          </Typography>

          {sucesso && (
            <Alert severity="success" icon={<ThumbUpIcon />} sx={{ mb: 2, borderRadius: 2 }}>
              <Typography fontWeight={600} fontSize="0.875rem">
                Recebemos sua sugestão! Nossa equipe vai analisar e implementar assim que possível. Obrigado! 🙏
              </Typography>
            </Alert>
          )}

          {erro && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{erro}</Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Título da sugestão"
              fullWidth size="small"
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              inputProps={{ maxLength: 200 }}
              placeholder="Ex: Adicionar filtro por período no relatório..."
            />

            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select value={categoria} label="Categoria" onChange={e => setCategoria(e.target.value)}>
                {CATEGORIAS.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrição detalhada"
              fullWidth multiline minRows={4}
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Descreva com detalhes o que você gostaria de ver no sistema, como funcionaria, e por que seria útil..."
            />

            <Button
              variant="contained" size="medium"
              startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              disabled={enviando}
              onClick={handleEnviar}
              sx={{
                alignSelf: 'flex-end',
                bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' },
                textTransform: 'none', fontWeight: 700, borderRadius: 2,
                boxShadow: `0 4px 14px ${ORANGE}40`,
                px: 3,
              }}
            >
              {enviando ? 'Enviando...' : 'Enviar sugestão'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Minhas sugestões ── */}
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#475569', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Minhas sugestões
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress sx={{ color: ORANGE }} size={32} />
        </Box>
      ) : sugestoes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, color: '#CBD5E1' }}>
          <LightbulbIcon sx={{ fontSize: 44, mb: 1 }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Você ainda não enviou nenhuma sugestão
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {sugestoes.map(s => {
            const st = STATUS_MAP[s.status] || STATUS_MAP.pendente;
            return (
              <Card key={s.id} elevation={0} sx={{
                borderRadius: 2.5, border: '1px solid #F1F5F9',
                transition: 'box-shadow 0.15s',
                '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: DARK }}>{s.titulo}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 0.3 }}>
                        {CATEGORIAS.find(c => c.value === s.categoria)?.label} · {fmtDate(s.createdAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={st.label}
                      size="small"
                      icon={st.icon}
                      sx={{
                        bgcolor: `${st.color}15`, color: st.color,
                        border: `1px solid ${st.color}30`,
                        fontWeight: 700, fontSize: '0.7rem',
                        '& .MuiChip-icon': { color: st.color },
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.6 }}>
                    {s.descricao}
                  </Typography>

                  {s.respostaAdmin && (
                    <>
                      <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                      <Box sx={{ bgcolor: `${BLUE}08`, borderRadius: 1.5, p: 1.5, border: `1px solid ${BLUE}18` }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: BLUE, mb: 0.3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Resposta da equipe
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#334155' }}>
                          {s.respostaAdmin}
                        </Typography>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
